import { useEffect, useState } from 'react';
import { type Metafield, type Worklog } from '@forecastify/shared';
import { Button, Dialog } from '@forecastify/ui';
import { useT } from '../../i18n';
import { Field } from '../card';
import { TaskSearchField, type TaskChoice } from './task-search-field';
import {
  START_OF_DAY,
  WorklogFields,
  emptyWorklogDraft,
  worklogHours,
  type WorklogDraft,
} from './worklog-form';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The calendar's entry dialog — `main.jsx` 3900–3958.                        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * One dialog for both cases: a drag produces a range with no task, a click on an entry produces
 * both. The mockup uses one too, and its title is the only thing that differs.
 *
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ IT ASKS THE SAME FIVE QUESTIONS AS EVERY OTHER LOGGING FORM NOW.      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Feedback round 4, three items at once, all of them about this dialog:
 *
 * — **Item 20.** It took a START and an END; the other two forms took a duration. What the column
 *   stores is a start and a length (`0017`), so the end was a number the product does not keep —
 *   converted at this dialog's edge and thrown away. It asks for the duration now, like everything
 *   else, and `worklog-form.tsx` is the form.
 * — **Item 19.** It could not mark overtime, which is the one act a worklog form is the only
 *   entrance to. The shared form carries the control, so this gets it by being the same form.
 * — **Item 21.** The task was a `Select` over whatever `GET /tasks` returned — fifty rows, the
 *   API's default page, with no way to reach the fifty-first. `task-search-field.tsx` is the
 *   quick-log's search, extracted.
 *
 * ⚠️ **A drag still produces a range, and it still lands here as a start and a length.** `rangeOf`
 * went with the end field; `startAndHours` replaces it, and it converts in the one direction that
 * survives the round trip.
 */
export type EntryDraft = WorklogDraft;

export function EntryDialog({
  draft,
  results,
  overtimeOffered = false,
  metafields = [],
  canDelete,
  onSearchTasks,
  onClose,
  onSave,
  onDelete,
}: {
  /** `null` closes the dialog. */
  readonly draft: EntryDraft | null;
  /** What the task search last returned. */
  readonly results: readonly TaskChoice[];
  /**
   * ⚠️ Whether the workspace defines any active overtime type — the form's marking control is
   * absent when it does not. The TYPES themselves are the approver's business since `0147`.
   */
  readonly overtimeOffered?: boolean;
  /** The workspace's `worklog` metafields. Empty when none are defined. */
  readonly metafields?: readonly Metafield[];
  readonly canDelete: boolean;
  readonly onSearchTasks: (query: string) => void;
  readonly onClose: () => void;
  readonly onSave: (draft: EntryDraft, hours: number) => void;
  readonly onDelete: (id: string) => void;
}) {
  const t = useT();
  const [local, setLocal] = useState<EntryDraft | null>(draft);
  const [pick, setPick] = useState<TaskChoice | null>(null);

  /* ⚠️ Re-seeded whenever a new draft arrives. Without this, dragging a second range while the
   * dialog is open would show the first one's times. */
  useEffect(() => {
    setLocal(draft);
    setPick(null);
  }, [draft]);

  if (local === null) return null;

  const hours = worklogHours(local);

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={local.id === undefined ? t('logs.entry.create') : t('logs.entry.edit')}
      description={t('logs.entry.hint')}
      closeLabel={t('common.close')}
    >
      <div className="flex flex-col gap-3">
        <Field label={t('time.log.task')}>
          <TaskSearchField
            pick={pick}
            results={results}
            onQuery={onSearchTasks}
            onPick={(task) => {
              setPick(task);
              setLocal({ ...local, taskId: task.id });
            }}
            onClear={() => {
              setPick(null);
              setLocal({ ...local, taskId: '' });
            }}
            swatch={(color) => (
              <span
                className="size-2 shrink-0 rounded-full"
                style={{ background: color ?? 'var(--muted-foreground)' }}
                aria-hidden
              />
            )}
          />
        </Field>

        <WorklogFields
          draft={local}
          onChange={setLocal}
          overtimeOffered={overtimeOffered}
          metafields={metafields}
          layout="stack"
        />

        <div className="flex items-center gap-2">
          {local.id !== undefined && canDelete && (
            /* ⚠️ A neutral outline, not the filled danger button — the kit reserves that strictly
             * for irreversible financial deletions, and a worklog is neither. */
            <Button
              variant="outline"
              onClick={() => {
                onDelete(local.id ?? '');
              }}
            >
              {t('common.delete')}
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            /* ⚠️ Disabled on an unpickable task as well as a bad duration — the mockup toasts
             * *"Válassz feladatot."* on submit instead, which is a refusal after the fact. */
            disabled={hours === null || local.taskId === ''}
            onClick={() => {
              if (hours !== null) onSave(local, hours);
            }}
          >
            {local.id === undefined ? t('time.log.submit') : t('common.save')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * A drag's range, or an existing row, as the draft this dialog edits.
 *
 * ⚠️⚠️ **THE RANGE COLLAPSES TO A START AND A LENGTH HERE, AND ONLY HERE.** The calendar thinks in
 * `from`/`to` because that is what a drag produces; `worklogs` stores `started_at` and `hours`
 * because that is what a day is made of (`0017`). Somewhere the two have to meet, and doing it at
 * the calendar's edge means the form never learns about ranges and the endpoint never learns a
 * calendar exists. It used to happen at the dialog's edge, in both directions — which is how the
 * dialog came to ask for an end time nothing stores.
 */
export function draftFromRange(
  day: string,
  from: string,
  to: string,
  minutes: (value: string) => number | null,
): EntryDraft {
  const start = minutes(from) ?? 540;
  const end = minutes(to) ?? start + 60;
  return {
    ...emptyWorklogDraft(day),
    startedAt: from,
    /* ⚠️ `H:MM`, the form's own spelling — it parses `2:30` and every other shape the shared helper
       takes, so handing it a decimal would be handing it a second format to support. */
    hours: `${String(Math.floor(Math.max(end - start, 0) / 60))}:${String(Math.max(end - start, 0) % 60).padStart(2, '0')}`,
  };
}

/** An existing entry, as the draft this dialog edits. */
export function draftFromEntry(entry: Worklog, formatted: string): EntryDraft {
  return {
    id: entry.id,
    version: entry.version,
    taskId: entry.taskId,
    workedOn: entry.workedOn,
    startedAt: entry.startedAt ?? START_OF_DAY,
    hours: formatted,
    note: entry.note ?? '',
    /* ⚠️ A claim is a separate row and this dialog does not edit one: re-saving an hour must not
       silently withdraw or duplicate the request attached to it. */
    overtime: false,
    metafields: entry.metafields,
  };
}
